import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dumbbell, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

export default function ResetPasswordPage() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { updatePassword } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast({
                title: 'Error',
                description: 'Las contraseñas no coinciden',
                variant: 'destructive',
            });
            return;
        }

        setIsLoading(true);
        const { success, error } = await updatePassword(password);
        if (success) {
            toast({
                title: '¡Contraseña actualizada!',
                description: 'Ahora puedes iniciar sesión con tu nueva contraseña.',
            });
            navigate('/login');
        } else {
            toast({
                title: 'Error',
                description: error || 'No se pudo actualizar la contraseña',
                variant: 'destructive',
            });
        }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-slate-50">
            <div className="w-full max-w-md animate-in fade-in zoom-in duration-500">
                <div className="flex flex-col items-center gap-4 mb-10 cursor-pointer" onClick={() => navigate('/')}>
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-600 shadow-xl shadow-orange-600/20">
                        <Dumbbell className="h-10 w-10 text-white" />
                    </div>
                    <span className="text-3xl font-black text-slate-900 tracking-tight">GymPro SV</span>
                </div>

                <Card className="border-none shadow-2xl shadow-slate-200/50 rounded-3xl overflow-hidden bg-white">
                    <div className="h-2 bg-gradient-to-r from-orange-600 to-amber-500" />
                    <CardHeader className="space-y-1 pb-4 pt-8 text-center px-10">
                        <CardTitle className="text-3xl font-black text-slate-900 leading-tight">
                            Nueva Contraseña
                        </CardTitle>
                        <CardDescription className="text-slate-500 font-medium">
                            Ingresa tu nueva contraseña para recuperar el acceso
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="px-10 pb-10">
                        <form onSubmit={handleResetPassword} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-xs font-bold uppercase text-slate-400 ml-1">Nueva Contraseña</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="h-12 pr-12 bg-slate-50 border-slate-100 focus:bg-white focus:border-orange-500/30 transition-all rounded-xl text-slate-900 font-semibold"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-0 top-0 h-12 w-12 hover:bg-transparent text-slate-400"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-xs font-bold uppercase text-slate-400 ml-1">Confirmar Contraseña</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="h-12 bg-slate-50 border-slate-100 focus:bg-white focus:border-orange-500/30 transition-all rounded-xl text-slate-900 font-semibold"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full h-14 text-lg font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-2xl transition-all shadow-lg hover:shadow-xl mt-4"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                        ACTUALIZANDO...
                                    </>
                                ) : (
                                    'RESTABLECER CONTRASEÑA'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
